import { useCallback, useEffect, useRef, useState } from 'react';
import type * as api from '../../lib/api.js';
import { SPHERES, type SphereId } from '@superposition/domain';
import { sphereColor } from '../../lib/palette.js';
import { useSphereName } from '../../lib/names.js';
import { useLakePractices } from '../../lib/profile.js';
import { haptic } from '../../lib/telegram.js';
import { useT } from '../../i18n/index.js';
import { BinauralEngine, browserAudioFactory, type AudioStatus } from './audio.js';
import {
  breathPhase,
  formatClock,
  isFinished,
  pauseSession,
  remainingMs,
  resumeSession,
  startSession,
  type LakeSession,
} from './timer.js';

const DURATIONS = [10, 20, 30] as const;
/** у сфер без записи — тон 180/188 гц на 20 минут, пока не появится своя практика */
const TONE_MINUTES = 20;

/** минуты записи в сетке 10/20/30: 11 минут — «10», 29 — «30» */
function minutesOf(p: api.LakePractice | undefined): number {
  if (!p) return TONE_MINUTES;
  return DURATIONS.reduce((best, m) => (Math.abs(m - p.duration / 60) < Math.abs(best - p.duration / 60) ? m : best), DURATIONS[0] as number);
}

export function LakeScreen({ initialSphere }: { initialSphere?: SphereId | null }): React.ReactNode {
  const sphereName = useSphereName();
  const t = useT();
  const [intention, setIntention] = useState<SphereId>(initialSphere ?? 'sostoyanie');
  const practices = useLakePractices() ?? [];
  const [session, setSession] = useState<LakeSession | null>(null);
  // запись медитации, которая играет в этой сессии; null — практика с тоном
  const recordRef = useRef<HTMLAudioElement | null>(null);
  const [recordFailed, setRecordFailed] = useState(false);

  const practice = practices.find((p) => p.sphere === intention);
  const minutes = minutesOf(practice);
  const [now, setNow] = useState(() => Date.now());
  const [audio, setAudio] = useState<AudioStatus>({ kind: 'off' });
  const [muted, setMuted] = useState(false);

  const engineRef = useRef<BinauralEngine | null>(null);
  const sessionSeq = useRef(0);

  if (engineRef.current === null) {
    engineRef.current = new BinauralEngine({ createContext: browserAudioFactory(t.lake.sound), texts: t.lake.sound });
  }
  const engine = engineRef.current;

  const stopAudio = useCallback((): void => {
    engine.stop();
    setAudio({ kind: 'off' });
  }, [engine]);

  const startAudio = useCallback(async (): Promise<void> => {
    setAudio({ kind: 'starting' });
    const status = await engine.start();
    setAudio(status);
  }, [engine]);

  const stopRecord = useCallback((): void => {
    recordRef.current?.pause();
    recordRef.current = null;
  }, []);

  const begin = useCallback((): void => {
    haptic('medium');
    sessionSeq.current += 1;
    setRecordFailed(false);
    if (practice) {
      // play() прямо в обработчике нажатия: иначе телефон не даст включить звук
      const el = new Audio(practice.src);
      el.preload = 'auto';
      recordRef.current = el;
      el.addEventListener('ended', () => {
        if (recordRef.current === el) {
          recordRef.current = null;
          setSession(null);
          haptic('medium');
        }
      });
      el.play().catch(() => setRecordFailed(true));
      const ms = (navigator as { mediaSession?: MediaSession }).mediaSession;
      if (ms && typeof MediaMetadata !== 'undefined') {
        ms.metadata = new MediaMetadata({ title: t.lake.mediaTitle({ title: t.lake.practiceTitle({ title: practice.title }) }), artist: t.common.brand });
      }
      setSession(startSession(sessionSeq.current, practice.duration * 1000, Date.now()));
    } else {
      setSession(startSession(sessionSeq.current, minutes * 60_000, Date.now()));
      if (!muted) void startAudio();
    }
    setNow(Date.now());
  }, [practice, minutes, muted, startAudio, t]);

  const leave = useCallback((): void => {
    stopAudio();
    stopRecord();
    setSession(null);
  }, [stopAudio, stopRecord]);

  // тик отображения; сам отсчёт идёт по дедлайну, тик только перерисовывает
  useEffect(() => {
    if (!session || session.endsAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [session]);

  // практика закончилась. у записи конец определяет сама запись (ended):
  // если она подгружалась, таймер не должен оборвать ее раньше
  useEffect(() => {
    if (session && !recordRef.current && isFinished(session, now)) {
      stopAudio();
      setSession(null);
      haptic('medium');
    }
  }, [session, now, stopAudio]);

  // уход со страницы: тон гасим и ставим практику на паузу. запись медитации
  // играет дальше — ее слушают с закрытыми глазами и погасшим экраном
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState !== 'hidden' || recordRef.current) return;
      engine.stop();
      setAudio({ kind: 'off' });
      setSession((s) => (s ? pauseSession(s, Date.now()) : s));
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [engine]);

  // уход с экрана не оставляет ни осцилляторов, ни интервалов, ни звука записи
  useEffect(
    () => () => {
      engine.dispose();
      recordRef.current?.pause();
      recordRef.current = null;
    },
    [engine],
  );

  const recorded = Boolean(recordRef.current);
  const togglePause = (): void => {
    const el = recordRef.current;
    if (!session) return;
    haptic('light');
    if (session.endsAt === null) {
      setSession(resumeSession(session, Date.now()));
      void el?.play().catch(() => setRecordFailed(true));
    } else {
      setSession(pauseSession(session, Date.now()));
      el?.pause();
    }
    setNow(Date.now());
  };

  if (!session) {
    return (
      <section className="fade" aria-labelledby="lake-title">
        <h2 className="screen-t" id="lake-title">
          {t.lake.title}
        </h2>
        <p className="lake-intro">{t.lake.intro}</p>

        <div className="int-grid" role="group" aria-label={t.lake.intentionAria}>
          {SPHERES.map((s) => {
            const c = sphereColor(s.id);
            const on = s.id === intention;
            return (
              <button
                type="button"
                key={s.id}
                className={`int${on ? ' sel' : ''}`}
                style={{ color: c, borderColor: on ? c : undefined }}
                aria-pressed={on}
                onClick={() => setIntention(s.id)}
              >
                <span className="dot" style={{ background: c }} />
                <span style={{ color: 'var(--ink)' }}>{sphereName(s.id)}</span>
                <span className="int-sub">
                  {practices.some((p) => p.sphere === s.id) ? t.lake.recorded({ minutes: minutesOf(practices.find((p) => p.sphere === s.id)) }) : t.lake.soon}
                </span>
              </button>
            );
          })}
        </div>

        <div className="times" role="group" aria-label={t.lake.durationAria}>
          {/* минуты заданы практикой сферы: запись длится столько, сколько длится */}
          {DURATIONS.map((m) => (
            <button type="button" key={m} className={m === minutes ? 'sel' : ''} aria-pressed={m === minutes} disabled={m !== minutes}>
              {t.lake.minutes({ minutes: m })}
            </button>
          ))}
        </div>

        <button type="button" className="lake-cta" onClick={begin}>
          {t.lake.enter}
        </button>

        {practice ? (
          <p className="lake-note">
            {t.lake.practiceNote({ title: t.lake.practiceTitle({ title: practice.title }), minutes: Math.round(practice.duration / 60) })}
          </p>
        ) : (
          <>
            <div className="lake-audio-row">
              <button type="button" aria-pressed={muted} onClick={() => setMuted((v) => !v)}>
                {muted ? t.lake.soundOff : t.lake.soundOn}
              </button>
            </div>
            <p className="lake-note">{t.lake.toneNote}</p>
          </>
        )}

        <p className="lake-soon">{t.lake.weekly}</p>
      </section>
    );
  }

  const left = remainingMs(session, now);
  const paused = session.endsAt === null;

  return (
    <section className="fade" aria-labelledby="lake-run-title">
      <h2 className="screen-t" id="lake-run-title">
        {t.lake.title}
      </h2>
      <div className="breathe-wrap">
        <div className="breathe">
          <span className="lbl">{paused ? t.lake.pause : breathPhase(session, now, t.lake.breath)}</span>
        </div>
        <div className="timer" role="timer" aria-live="off">
          {formatClock(left)}
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 13.5, marginBottom: 20 }}>
          {recorded && practice ? t.lake.meditation({ title: t.lake.practiceTitle({ title: practice.title }) }) : t.lake.intention({ sphere: sphereName(intention) })}
        </div>

        {recorded ? (
          <button type="button" className="lake-cta" onClick={togglePause}>
            {paused ? t.lake.resume : t.lake.pause}
          </button>
        ) : null}
        {recordFailed ? (
          <p className="lake-error">{t.lake.recordFailed}</p>
        ) : null}

        {paused && !recorded ? (
          <button
            type="button"
            className="lake-cta"
            onClick={() => {
              setSession((s) => (s ? resumeSession(s, Date.now()) : s));
              setNow(Date.now());
              if (!muted) void startAudio();
            }}
          >
            {t.lake.resume}
          </button>
        ) : null}

        <button type="button" className="lake-cta lake-stop" onClick={leave}>
          {t.lake.leave}
        </button>

        <div className="lake-audio-row" hidden={recorded}>
          {audio.kind === 'playing' ? (
            <button type="button" onClick={stopAudio}>
              {t.lake.stopSound}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMuted(false);
                void startAudio();
              }}
              disabled={audio.kind === 'starting'}
            >
              {audio.kind === 'starting' ? t.lake.startingSound : t.lake.startSound}
            </button>
          )}
        </div>

        {audio.kind === 'failed' ? (
          <p className="lake-error">{t.lake.soundFailed({ message: audio.message })}</p>
        ) : null}
        {audio.kind === 'playing' && !audio.stereo ? (
          <p className="lake-note">{t.lake.noStereo}</p>
        ) : null}
      </div>
    </section>
  );
}
